/* eslint-disable react/prop-types */
import React, {memo} from 'react';
import {BrowserRouter, Redirect, Route, RouteProps, Switch} from 'react-router-dom';
import {isObject, omit} from 'lodash';
import {AdminRole} from '~/types/models/admin';
import {IconType} from 'react-icons/lib';
import {CustomSwitch} from '~/components/elements/CustomSwitch';
import {checkPermissions, getObjNthItem} from '~/utils/helpers';

export interface BasicRoute extends RouteProps {
  position?: 'left' | 'right';
  path: string;
  redirect?: string | Record<string, string>;
  routes?: BasicRoute[];
  action?: 'new-tab';
  label?: string;
  icon?: IconType;
  class?: string;
  roles?: string[];
}

export interface PrivateRoute extends BasicRoute {
  routes?: PrivateRoute[];
}

export interface AppRouterProps {
  basename?: string;
  authorities?: AdminRole[];
  defaultRedirect: string;
  privateRoutes?: PrivateRoute[];
  publicRoutes?: BasicRoute[];
  notFoundPage?: React.FunctionComponent<any>;
  unauthorizedPage?: React.FunctionComponent<any>;
}

const omitRouteRenderProps = (route: BasicRoute) => {
  return omit(route, ['render', 'component']);
};

const NotFoundPageDefault = () => {
  return (
    <div>
      <h1>This is a not found page by default</h1>
    </div>
  );
};

const UnauthorizedPageDefault = () => {
  return (
    <div>
      <h1>This is an unauthorized page by default</h1>
    </div>
  );
};

export const EmptyLayout = memo(({children}: {children: React.ReactNode}) => {
  return <div>{children}</div>;
});

export const AppRouter = memo(function AppRouter({
  basename,
  authorities,
  defaultRedirect,
  privateRoutes = [],
  publicRoutes = [],
  notFoundPage: NotFoundPage = NotFoundPageDefault,
  unauthorizedPage: UnauthorizedPage = UnauthorizedPageDefault,
}: AppRouterProps) {
  const renderRedirectRoute = (route: BasicRoute, currentRoles: AdminRole[] = []) => {
    if (isObject(route.redirect)) {
      let redirectTo = getObjNthItem(route.redirect, 1);
      Object.entries(route.redirect).forEach(([role, path]) => {
        const hasRole = currentRoles.findIndex(roles => roles.value === role);
        if (hasRole !== -1) {
          redirectTo = path;
        }
      });

      return (
        <Route
          key={`redirect-${route.path}`}
          {...omitRouteRenderProps(route)}
          render={() => <Redirect to={redirectTo || defaultRedirect} />}
        />
      );
    }

    return (
      <Route
        key={`redirect-${route.path}`}
        {...omitRouteRenderProps(route)}
        render={() => <Redirect to={route.redirect || defaultRedirect} />}
      />
    );
  };

  const renderPublicRoute = (route: BasicRoute, parentPath = '') => {
    const {path, component: RouteComponent = EmptyLayout, exact} = route;
    const fullPath = parentPath && parentPath !== '/' ? `${parentPath}${path}` : path;

    // Handle redirect URLs
    if (route.redirect) {
      return renderRedirectRoute(route);
    }

    if (route.routes) {
      const subRoutes = route?.routes?.map(subRoute => renderPublicRoute(subRoute, fullPath));

      return (
        <Route
          key={`public-${fullPath}`}
          exact={exact === undefined ? true : exact}
          {...omitRouteRenderProps(route)}
          path={fullPath}
          render={props => (
            <RouteComponent {...props}>
              <Switch>{subRoutes}</Switch>
            </RouteComponent>
          )}
        />
      );
    }

    return (
      <Route
        key={`public-${fullPath}`}
        exact={exact === undefined ? true : exact}
        {...omitRouteRenderProps(route)}
        path={fullPath}
        render={props => <RouteComponent {...props} />}
      />
    );
  };

  const renderPrivateRoute = (route: PrivateRoute, parentPath = '') => {
    const {roles, redirect, path, component: RouteComponent = EmptyLayout, exact} = route;
    const hasPermission = checkPermissions(authorities, roles);
    const fullPath = parentPath && parentPath !== '/' ? `${parentPath}${path}` : path;
    if (redirect) {
      return renderRedirectRoute(route, authorities);
    }
    if (!hasPermission) {
      if (redirect) {
        return renderRedirectRoute(route, authorities);
      }
      return (
        <Route
          key={`private-${fullPath}`}
          {...omitRouteRenderProps(route)}
          path={fullPath}
          render={props => <UnauthorizedPage {...props} />}
        />
      );
    }

    if (route.routes) {
      const subRoutes = route?.routes?.map(subRoute => renderPrivateRoute(subRoute, fullPath));
      return (
        <Route
          key={`private-${fullPath}`}
          exact={exact === undefined ? true : exact}
          {...omitRouteRenderProps(route)}
          path={fullPath}
          render={props => {
            return (
              <RouteComponent {...props}>
                <Switch>{subRoutes}</Switch>
              </RouteComponent>
            );
          }}
        />
      );
    }

    return (
      <Route
        key={`private-${fullPath}`}
        exact={exact === undefined ? true : exact}
        {...omitRouteRenderProps(route)}
        path={fullPath}
        render={props => <RouteComponent {...props} />}
      />
    );
  };

  const renderNotFoundRoute = () => {
    return <Route path="*" key="notfound-route" render={props => <NotFoundPage {...props} />} />;
  };

  return (
    <BrowserRouter basename={basename}>
      <CustomSwitch>
        {publicRoutes.map(route => {
          return renderPublicRoute(route);
        })}
        {privateRoutes.map(route => {
          return renderPrivateRoute(route);
        })}
        {renderNotFoundRoute()}
      </CustomSwitch>
    </BrowserRouter>
  );
});
